import React, { useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Dialog, ElevatedButton, TextButton, TextInput } from '@producer/ui-kit';

import { plainDateFromLegacy, type PlainDate } from '../../../core/services/plain-date';

interface DateSelectionModalProps {
  onSubmit: (date: string | null) => Promise<void>;
  onCancel: () => void;
  fieldName: 'dueDate' | 'startDate';
  value?: PlainDate;
  dateValidator?: (date: string) => boolean;
}

export const DateSelectionModal: React.FC<DateSelectionModalProps> = ({
  onSubmit,
  onCancel,
  fieldName,
  value,
  dateValidator,
}) => {
  const { t } = useTranslation('step');
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);

  const schema = useMemo(
    () =>
      yup.object({
        date: yup.string().nullable(),
      }),
    [],
  );

  const {
    handleSubmit,
    register,
    formState: { errors },
    setError,
    clearErrors,
    setValue,
  } = useForm({
    defaultValues: {
      // `value` is a STORED value, not necessarily one that already came out of an
      // `<input type="date">`. On any database the plain-date migration hasn't run against, it
      // can still be a legacy ISO instant, which the native input's value-sanitization algorithm
      // silently discards, rendering the field blank. Normalise through `plainDateFromLegacy` so
      // a legacy value shows the day it means instead.
      date: (value && plainDateFromLegacy(value)) ?? '',
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (!dateValidator || !value) return;

    const isValid = dateValidator(value.toString());
    if (isValid) return;

    const message =
      fieldName === 'dueDate'
        ? 'Due date cannot be earlier than the start date.'
        : "Start date can't be after due date.";

    setIsDisabled(true);
    setError('date', { message });
  }, []);

  const onHandleSubmit = async (values: any) => {
    if (onSubmit && !isLoading) {
      setIsLoading(true);

      // `<input type="date">` already produces YYYY-MM-DD, which is exactly what we store. The
      // previous version turned it into an instant at local noon, which is what made the same day
      // read differently for different viewers.
      await onSubmit(values.date || null);

      setIsLoading(false);
      onCancel();
    }
  };

  const handleDateRevalidate = (input: any) => {
    if (!dateValidator) {
      setValue('date', input.target.value);
      return;
    }

    const isValid = dateValidator(input.target.value);
    if (isValid) {
      setIsDisabled(false);
      setValue('date', input.target.value);
      clearErrors('date');
      return;
    }

    const message =
      fieldName === 'dueDate'
        ? 'Due date cannot be earlier than the start date.'
        : "Start date can't be after due date.";

    setIsDisabled(true);
    setError('date', { message });
  };

  return (
    <Dialog
      title={fieldName === 'startDate' ? 'Set start date' : 'Set due date'}
      onClose={onCancel}
      actions={[
        <TextButton key="cancel" text="Cancel" onClick={onCancel} />,
        <ElevatedButton
          key="submit"
          isLoading={isLoading}
          disabled={isLoading || isDisabled}
          onClick={handleSubmit(onHandleSubmit)}
          text="Update"
        />,
      ]}
    >
      <form onSubmit={handleSubmit(onHandleSubmit)}>
        <TextInput
          type="date"
          {...register('date', dateValidator && { validate: { dateValidator } })}
          label={t(fieldName === 'dueDate' ? 'due_date' : 'start_date') || ''}
          errorMessage={errors.date?.message}
          onChange={handleDateRevalidate}
        />
      </form>
    </Dialog>
  );
};
